/**
 * InputBindings — pure, framework-free binding config for the input system.
 *
 * Deliberately imports NOTHING (no Phaser, no DOM) so it is fully unit-testable
 * in a node environment. The Phaser polling adapter lives in InputManager.ts.
 */

export type GameAction =
  | 'UP'
  | 'DOWN'
  | 'LEFT'
  | 'RIGHT'
  | 'LIGHT'
  | 'HEAVY'
  | 'JUMP'
  | 'SPECIAL'
  | 'PICKUP'
  | 'BLOCK'
  | 'BACKUP'
  | 'DODGE'
  | 'TAUNT';

export type PlayerId = 1 | 2;

/** Action → Phaser KeyboardPlugin key name (e.g. 'W', 'SPACE', 'UP'). */
export type KeyBindings = Record<GameAction, string>;

export const ACTIONS: readonly GameAction[] = [
  'UP',
  'DOWN',
  'LEFT',
  'RIGHT',
  'LIGHT',
  'HEAVY',
  'JUMP',
  'SPECIAL',
  'PICKUP',
  'BLOCK',
  'BACKUP',
  'DODGE',
  'TAUNT',
];

export const P1_DEFAULTS: KeyBindings = {
  UP: 'W',
  DOWN: 'S',
  LEFT: 'A',
  RIGHT: 'D',
  LIGHT: 'J',
  HEAVY: 'K',
  JUMP: 'SPACE',
  SPECIAL: 'L',
  PICKUP: 'U',
  BLOCK: 'I',
  BACKUP: 'R',
  DODGE: 'SHIFT',
  TAUNT: 'T',
};

export const P2_DEFAULTS: KeyBindings = {
  UP: 'UP',
  DOWN: 'DOWN',
  LEFT: 'LEFT',
  RIGHT: 'RIGHT',
  LIGHT: 'NUMPAD_ONE',
  HEAVY: 'NUMPAD_TWO',
  JUMP: 'NUMPAD_ZERO',
  SPECIAL: 'NUMPAD_THREE',
  PICKUP: 'NUMPAD_FOUR',
  BLOCK: 'NUMPAD_FIVE',
  BACKUP: 'NUMPAD_SIX',
  DODGE: 'NUMPAD_SEVEN',
  TAUNT: 'NUMPAD_EIGHT',
};

/**
 * W3C Standard Gamepad button index → action.
 * 0=A/✕ 1=B/○ 2=X/□ 3=Y/△ 4=LB 5=RB · 12-15 = d-pad U/D/L/R.
 */
export const GAMEPAD_BUTTON_TO_ACTION: Record<number, GameAction> = {
  0: 'LIGHT',
  1: 'JUMP',
  2: 'PICKUP',
  3: 'SPECIAL',
  4: 'BLOCK',
  5: 'HEAVY',
  6: 'DODGE',
  12: 'UP',
  13: 'DOWN',
  14: 'LEFT',
  15: 'RIGHT',
};

export const STORAGE_KEY = 'manor.bindings.v1';
export const AXIS_DEADZONE = 0.5;

function clone(b: KeyBindings): KeyBindings {
  return { ...b };
}

/** Pure binding store — no Phaser, no DOM. Safe to unit-test. */
export class BindingStore {
  private p1: KeyBindings = clone(P1_DEFAULTS);

  private p2: KeyBindings = clone(P2_DEFAULTS);

  get(player: PlayerId): KeyBindings {
    return clone(player === 1 ? this.p1 : this.p2);
  }

  setBind(player: PlayerId, action: GameAction, key: string): void {
    const target = player === 1 ? this.p1 : this.p2;
    target[action] = key.toUpperCase();
  }

  reset(player?: PlayerId): void {
    if (player === undefined || player === 1) this.p1 = clone(P1_DEFAULTS);
    if (player === undefined || player === 2) this.p2 = clone(P2_DEFAULTS);
  }

  serialize(): string {
    return JSON.stringify({ p1: this.p1, p2: this.p2 });
  }

  load(json: string): boolean {
    try {
      const parsed = JSON.parse(json) as { p1?: Partial<KeyBindings>; p2?: Partial<KeyBindings> };
      if (parsed.p1) this.p1 = { ...P1_DEFAULTS, ...parsed.p1 };
      if (parsed.p2) this.p2 = { ...P2_DEFAULTS, ...parsed.p2 };
      return true;
    } catch {
      return false;
    }
  }
}
